/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState } from "react"
import { Buttons, Card } from "components/Card/Card"
import { useDispatch, useSelector } from "react-redux"
import { getVitalsNeededByReason, getVitalsToAsk } from "./utils"
import { Keyboard } from "hocs/withKeyboard"
import { Input } from "components/Input"
import { Button } from "components/Button"
import vitalsActions from "containers/Vitals/services/action"
import surveyActions from "containers/Survey/services/actions"
import {
  getCurrentPatient,
  getCurrentSelectedPatientId,
  getDoctorTypeAnswer,
  getReasonAnswer,
  isCurrentPatientUnresolved,
} from "containers/Survey/services/selector"
import { languages } from "locales/languages"
import { cdn } from "core/cdn"
import { useNavigate } from "react-router"
import { SURVEY_PAGE } from "core/constants"
import { getSelectedPatientVitals } from "./services/selector"
import { Steps } from "components/Steps"
import PreviousButton from "components/Previous/PreviousButton"
import { vitals as vitalList } from "./services/constants"
import { Title } from "components/Title/Title"
import Temperature from "components/Input/Temperature/Temperature"
import { vitalsType } from "types/payload"
import { VitalsStore } from "types/store"
import Tension from "./components/Tension"
// import { Modal } from 'antd'
import { GenericModal } from "components/Modal/Modal"

import { useStepper } from "components/Steps/hooks"
import { DoctorType } from "types/entity"

const SaturationForm = ({ currentValue, setCurrentValue, onSkip }) => {
  const videoRef = useRef(null)
  // a prefilled value means it was already recorded: skip the tutorial
  const [step, setStep] = useState<"tutorial" | "input">(
    currentValue ? "input" : "tutorial",
  )
  if (step === "tutorial") {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
        }}
      >
        <Title text={languages.takeYourSaturation} doNotAnimateTitle={true} />
        <video
          style={{
            borderRadius: "15px",
            width: "-webkit-fill-available",
            maxHeight: "500px",
            marginTop: "20px",
            marginBottom: "40px",
          }}
          width={960}
          height={480}
          autoPlay
          id="remote-media"
          muted
          ref={videoRef}
          onEnded={() => {
            videoRef?.current?.play()
          }}
        >
          <source src={cdn("/videos/tutos/oxymetre.mp4")} type="video/mp4" />
          {languages.unableToProcessVideo}
        </video>
        <Buttons>
          <Button wide="long" htmlType="button" onClick={onSkip}>
            {languages.skipStep}
          </Button>
          <Button
            wide="long"
            htmlType="button"
            type="primary"
            onClick={() => setStep("input")}
          >
            {languages.iTookMySaturation}
          </Button>
        </Buttons>
      </div>
    )
  }
  if (step === "input") {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
        }}
      >
        <Title text={languages.addYourSaturation} doNotAnimateTitle={true} />
        <img src={cdn("images/saturation.svg")} alt="scale" width={120} />
        <Keyboard
          options={{ type: "code", unit: "%SpO2", inputName: "saturation" }}
          value=""
          onChange={(value) => {
            setCurrentValue(value)
          }}
        >
          <Input
            value={currentValue}
            name="saturation"
            width={30}
            maxLength={3}
          />
        </Keyboard>
        <Button
          type="primary"
          wide="long"
          htmlType="submit"
          disabled={!currentValue || currentValue === 0}
        >
          {languages.confirm}
        </Button>
        <Button type="link" htmlType="button" wide="long" onClick={onSkip}>
          {languages.skipStep}
        </Button>
      </div>
    )
  }
}

const WeightForm = ({ currentValue, setCurrentValue, onSkip }) => {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "column",
      }}
    >
      <img src={cdn("images/scale.svg")} alt="scale" width={120} />
      <Keyboard
        options={{ type: "code", unit: "kg", inputName: "weight" }}
        value=""
        onChange={(value) => {
          setCurrentValue(value)
        }}
      >
        <Input
          type="text"
          value={currentValue}
          name="weight"
          width={30}
          maxLength={3}
        />
      </Keyboard>
      <Button
        type="primary"
        wide="long"
        htmlType="submit"
        disabled={!currentValue || currentValue === "0"}
      >
        {languages.confirm}
      </Button>
      <Button type="link" htmlType="button" wide="long" onClick={onSkip}>
        {languages.skipStep}
      </Button>
    </div>
  )
}

const HeightForm = ({ currentValue, setCurrentValue, onSkip }) => {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "column",
      }}
    >
      <img src={cdn("images/height.svg")} alt="height" width={120} />
      <Keyboard
        options={{ type: "code", unit: "cm", inputName: "height" }}
        value=""
        onChange={(value) => {
          setCurrentValue(value)
        }}
      >
        <Input
          type="text"
          value={currentValue}
          name="height"
          width={30}
          maxLength={3}
        />
      </Keyboard>
      <Button
        type="primary"
        wide="long"
        htmlType="submit"
        disabled={!currentValue || currentValue === "0"}
      >
        {languages.confirm}
      </Button>
      <Button type="link" htmlType="button" wide="long" onClick={onSkip}>
        {languages.skipStep}
      </Button>
    </div>
  )
}

const TemperatureForm = ({ currentValue, setCurrentValue, onSkip }) => {
  const videoRef = useRef(null)
  // a prefilled value means it was already recorded: skip the tutorial
  const [step, setStep] = useState<"tutorial" | "input">(
    currentValue ? "input" : "tutorial",
  )
  if (step === "tutorial") {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          width: "100%",
        }}
      >
        <Title text={languages.takeYourTemperature} />
        <video
          style={{
            borderRadius: "15px",
            width: "-webkit-fill-available",
            maxHeight: "500px",
            marginTop: "20px",
            marginBottom: "40px",
          }}
          width={960}
          height={480}
          autoPlay
          id="remote-media"
          muted
          ref={videoRef}
          onEnded={() => {
            videoRef?.current?.play()
          }}
        >
          <source src={cdn("/videos/tutos/thermometre.mp4")} type="video/mp4" />
          {languages.unableToProcessVideo}
        </video>
        <Buttons>
          <Button wide="long" htmlType="button" onClick={onSkip}>
            {languages.skipStep}
          </Button>
          <Button
            wide="long"
            htmlType="button"
            type="primary"
            onClick={() => setStep("input")}
          >
            {languages.iTookMyTemperature}
          </Button>
        </Buttons>
      </div>
    )
  }
  if (step === "input") {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          width: "100%",
        }}
      >
        <Title text={languages.addYourTemperature} />
        <img
          src={cdn("/images/thermometer.svg")}
          alt="thermometer"
          width={120}
        />
        <Keyboard
          options={{ type: "twoFa", inputName: "temperature" }}
          value=""
          onChange={(value) => {
            setCurrentValue(value)
          }}
        >
          <Temperature value={currentValue} name="temperature" />
        </Keyboard>
        <Button
          wide="long"
          type="primary"
          htmlType="submit"
          disabled={
            !currentValue ||
            currentValue === 0 ||
            currentValue.toString().length < 3
          }
        >
          {languages.confirm?.toUpperCase()}
        </Button>
        <Button wide="long" htmlType="button" type="link" onClick={onSkip}>
          {languages.skipStep}
        </Button>
      </div>
    )
  }
}

const getTitle = (type) => {
  switch (type) {
    case vitalList.WEIGHT:
      return languages.addYourWeight
    case vitalList.HEIGHT:
      return languages.addYourHeight
    default:
      return undefined
  }
}

// Build the input value from the vital already recorded for this
// consultation (e.g. coming back to a completed step after a reload),
// reversing the transformations applied on submit
const getRecordedValue = (type, recorded) => {
  const value = recorded?.value
  if (value === undefined || value === null) return ""
  if (type === vitalList.TENSION) {
    if (
      value.systolic?.raw === undefined ||
      value.diastolic?.raw === undefined ||
      value.raw === undefined
    ) {
      return ""
    }
    return { sys: value.systolic.raw, dia: value.diastolic.raw, bpm: value.raw }
  }
  if (value.raw === undefined || value.raw === null) return ""
  if (type === vitalList.TEMPERATURE) {
    // recorded as "37,2", the keyboard input expects the digits "372"
    return String(value.raw).replace(/\D/g, "")
  }
  return String(value.raw)
}

export const getCurrentTypeUnit = (type) => {
  switch (type) {
    case vitalList.WEIGHT:
      return "Kg"
    case vitalList.SATURATION:
      return "% SpO2"
    case vitalList.TENSION:
      return "mmHg"
    case vitalList.TEMPERATURE:
      return "°"
    case vitalList.HEARTRATE:
      return "bpm"
    case vitalList.HEIGHT:
      return "cm"
    case vitalList.BMI:
      return "kg/m²"
    default:
      return undefined
  }
}
export function Vitals() {
  const navigate = useNavigate()
  // const { questions } = useSelector(
  //   ({ survey }: { survey: SurveyStore }) => survey,
  // )
  const doctorType = useSelector(getDoctorTypeAnswer) as DoctorType
  const reasonAnswer = useSelector(getReasonAnswer) as string | undefined
  const currentReason =
    doctorType === DoctorType.CHECKUP ? "CHECKUP" : reasonAnswer
  const vitals = useSelector(getSelectedPatientVitals)
  const vitalsStore = useSelector(
    ({ vitals }: { vitals: Partial<VitalsStore> }) => vitals,
  )
  const selectedPatientId = useSelector(getCurrentSelectedPatientId) as
    | number
    | undefined
  const restoredStep = vitalsStore?.currentStep
  const currentPatient = useSelector(getCurrentPatient)
  const isPatientUnresolved = useSelector(isCurrentPatientUnresolved)
  // weight is asked at every consultation for a patient under 18
  const vitalsNeededByReason = getVitalsNeededByReason(
    currentReason,
    currentPatient?.birthdate,
  )
  // In a teleconsultation launched from a check-up, skip the vitals that
  // check-up recorded for this patient
  const vitalsNeeded =
    doctorType === DoctorType.CHECKUP
      ? vitalsNeededByReason
      : getVitalsToAsk(
          vitalsNeededByReason,
          vitalsStore?.checkupVitals,
          selectedPatientId,
        )
  const isWeightAlwaysNeeded =
    vitalsNeeded.find((v) => v.label === vitalList.WEIGHT)?.alwaysNeeded ||
    false
  const [wrongValueModal, setWrongValueModal] = useState<boolean>(false)
  const [currentStep, setCurrentStep] = useState<number>(0)
  const isFirstStepRender = useRef(true)
  // set when the weight step was skipped on the way in (already recorded)
  const weightStepSkipped = useRef(false)
  const [currentType, setCurrentType] = useState<vitalsType>(
    vitalsNeeded?.[currentStep]?.label,
  )
  const [currentValue, setCurrentValue] = useState<any>(undefined)
  const [pendingVitalCreate, setPendingVitalCreate] = useState<boolean>(false)
  const dispatch = useDispatch()
  useEffect(() => {
    if (
      [
        vitalList.HEIGHT,
        vitalList.WEIGHT,
        vitalList.TEMPERATURE,
        vitalList.SATURATION,
        vitalList.TENSION,
      ].includes(vitalsNeeded?.[currentStep]?.label || "")
    ) {
      const label = vitalsNeeded[currentStep]?.label
      setCurrentValue(getRecordedValue(label, vitals?.[label]))
      setCurrentType(label as any)
      setPendingVitalCreate(false)
      // Mirror the position in the store so a reload restores it. The mount
      // value is skipped: it would overwrite the persisted step before the
      // hydration saga has read it back.
      if (isFirstStepRender.current) {
        isFirstStepRender.current = false
      } else {
        dispatch(vitalsActions.setVitalsStep(currentStep))
      }
    } else {
      dispatch(surveyActions.vitalsCompleted({ pendingVitalCreate }))
      setPendingVitalCreate(false)
      // the vitals flow is over, a future visit starts from the beginning
      dispatch(vitalsActions.setVitalsStep(undefined))
    }
  }, [currentStep])

  useEffect(() => {
    // A reload restores the persisted position through HYDRATE_VITALS after
    // this page has already mounted on step 0: adopt it when it arrives
    if (restoredStep !== undefined && restoredStep !== currentStep) {
      setCurrentStep(restoredStep)
    }
  }, [restoredStep])

  useEffect(() => {
    // weight is not alwaysAsked, if set, go to the next step
    // (the recorded weight can arrive after mount, e.g. on a page reload).
    // When a restored position is waiting to be adopted (reload hydration
    // delivers the weight and the position in the same render), let the
    // adoption win instead of pushing to the step after weight.
    if (restoredStep !== undefined && restoredStep !== currentStep) {
      return
    }
    // The selected relative may not be loaded yet (relatives are fetched
    // separately): wait for it instead of treating a possible minor as an
    // adult, the effect runs again once the patient is resolved.
    if (isPatientUnresolved) {
      return
    }
    if (
      vitals?.[vitalList.WEIGHT]?.value &&
      vitalsNeeded?.[currentStep]?.label === vitalList.WEIGHT &&
      !isWeightAlwaysNeeded
    ) {
      weightStepSkipped.current = true
      setCurrentStep(currentStep + 1)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    vitals?.[vitalList.WEIGHT]?.value?.raw,
    isPatientUnresolved,
    isWeightAlwaysNeeded,
  ])

  const vitalsCount = vitalsNeeded.length
  const insideStep = currentStep + 1
  const totalInsideSteps = vitalsCount
  const { current, totalSteps } = useStepper()

  return (
    <>
      <Steps
        current={current}
        insideStep={insideStep}
        totalInsideSteps={totalInsideSteps}
        totalSteps={totalSteps}
      />
      <PreviousButton
        onClick={() => {
          if (currentStep > 0) {
            if (
              vitalsNeeded[currentStep - 1]?.label === vitalList.WEIGHT &&
              weightStepSkipped.current
            ) {
              // the weight step was skipped on the way in, so going back from
              // the step after it leaves the page: drop the stored position so
              // a later visit (possibly with another reason) starts from the
              // beginning
              dispatch(vitalsActions.setVitalsStep(undefined))
              dispatch(surveyActions.previous())
              navigate(SURVEY_PAGE)
            } else {
              setCurrentStep(currentStep - 1)
            }
          } else {
            dispatch(vitalsActions.setVitalsStep(undefined))
            dispatch(surveyActions.previous())
            navigate(SURVEY_PAGE)
          }
        }}
      />
      <Card title={getTitle(currentType)} doNotAnimateTitle={true}>
        <form
          onSubmit={(e) => {
            e.preventDefault()
            let rawValue = currentValue
            if (
              currentType === vitalList.TEMPERATURE &&
              currentValue?.length === 3
            ) {
              // validate value before sending it
              if (currentValue < 320 || currentValue > 450) {
                setWrongValueModal(true)
                return
              }
              const splittedValue = currentValue.split("")
              rawValue =
                splittedValue[0] + splittedValue[1] + "," + splittedValue[2]
            }
            if (currentType === vitalList.SATURATION) {
              if (currentValue > 100 || currentValue < 70) {
                setWrongValueModal(true)
                return
              }
            }
            if (currentType === vitalList.TENSION) {
              dispatch(
                vitalsActions.createVitals({
                  type: "heartrate",
                  value: { raw: rawValue.bpm, unit: "bpm", comparator: "=" },
                  note: "",
                }),
              )
              dispatch(
                vitalsActions.createVitals({
                  type: "bp",
                  value: {
                    systolic: {
                      raw: rawValue.sys,
                      unit: getCurrentTypeUnit(vitalList.TENSION),
                      comparator: "=",
                    },
                    diastolic: {
                      raw: rawValue.dia,
                      unit: getCurrentTypeUnit(vitalList.TENSION),
                      comparator: "=",
                    },
                    raw: rawValue.bpm,
                    unit: "bpm",
                    comparator: "=",
                  },
                  note: "",
                }),
              )
              setPendingVitalCreate(true)
            } else if (rawValue) {
              dispatch(
                vitalsActions.createVitals({
                  type: currentType,
                  value: {
                    raw: rawValue,
                    unit: getCurrentTypeUnit(currentType),
                    comparator: "=",
                  },
                  note: "",
                }),
              )
              setPendingVitalCreate(true)
            }
            setCurrentStep(currentStep + 1)
          }}
        >
          {currentType === vitalList.HEIGHT && (
            <HeightForm
              currentValue={currentValue}
              setCurrentValue={setCurrentValue}
              onSkip={() => setCurrentStep(currentStep + 1)}
            />
          )}
          {currentType === vitalList.WEIGHT && (
            <WeightForm
              currentValue={currentValue}
              setCurrentValue={setCurrentValue}
              onSkip={() => {
                // drop the recorded weight so a skipped step never reuses a
                // previous value (weight is asked again for a patient under 18)
                dispatch(
                  vitalsActions.unsetVitalsByTypeSaga({
                    type: vitalList.WEIGHT,
                  }),
                )
                setCurrentStep(currentStep + 1)
              }}
            />
          )}
          {currentType === vitalList.TEMPERATURE && (
            <TemperatureForm
              currentValue={currentValue}
              setCurrentValue={setCurrentValue}
              onSkip={() => {
                dispatch(
                  vitalsActions.unsetVitalsByTypeSaga({
                    type: vitalList.TEMPERATURE,
                  }),
                )
                setCurrentStep(currentStep + 1)
              }}
            />
          )}
          {currentType === vitalList.SATURATION && (
            <SaturationForm
              currentValue={currentValue}
              setCurrentValue={setCurrentValue}
              onSkip={() => {
                dispatch(
                  vitalsActions.unsetVitalsByTypeSaga({
                    type: vitalList.SATURATION,
                  }),
                )
                setCurrentStep(currentStep + 1)
              }}
            />
          )}
          {currentType === vitalList.TENSION && (
            <Tension
              currentValue={currentValue}
              setCurrentValue={setCurrentValue}
              onSkip={() => {
                dispatch(
                  vitalsActions.unsetVitalsByTypeSaga({
                    type: vitalList.TENSION,
                  }),
                )
                dispatch(
                  vitalsActions.unsetVitalsByTypeSaga({
                    type: vitalList.HEARTRATE,
                  }),
                )
                setCurrentStep(currentStep + 1)
              }}
            />
          )}
        </form>
        <GenericModal
          style={{ width: "95vw" }}
          visible={wrongValueModal}
          closable={false}
          footer={<></>}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <img src={cdn("/images/warning_picto.svg")} alt="warning_picto" />
            <br />
            <h2 style={{ color: "#211452" }}>
              {languages.correctYourVitalsValue(currentType)}
            </h2>
            <br />
            <Button
              type="primary"
              wide="long"
              onClick={() => setWrongValueModal(false)}
            >
              {languages.fix?.toUpperCase()}
            </Button>
          </div>
        </GenericModal>
      </Card>
    </>
  )
}
